import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { NotFoundError } from 'src/shared/error';

import { getViewerSignature } from 'src/app/shared/api/signature';

import type {
	GammaPetitionViewerInfoQuery,
	GammaPetitionViewerInfoQueryVariables,
} from './gammaPetitionViewerInfo.graphql';
import { GammaPetitionViewerInfo } from './gammaPetitionViewerInfo.graphql';

export type PetitionViewerInfo = {
	readonly ownPetition: boolean;
	readonly canEdit: boolean;
	readonly signed: boolean;
};

/**
 * Fetches info about how the viewer (current user) relates to the petition.
 */
export async function getPetitionViewerInfo(
	utilityContext: UtilityContext,
	slug: string,
	currentUserId: string | undefined,
): Promise<PetitionViewerInfo> {
	// The currentUserId will be undefined for a guest viewer. The queries below can handle a missing ID on their own,
	// but short-circuiting those fetches can save us some time when the page is loading for guests (a very common
	// case).
	if (!currentUserId) {
		return {
			ownPetition: false,
			canEdit: false,
			signed: false,
		};
	}

	const [signature, petitionInfo] = await Promise.all([
		getViewerSignature(slug, utilityContext),
		getUserPetitionInfo(slug, utilityContext),
	]);

	const ownPetition = petitionInfo.user.id === currentUserId;
	const canEdit = petitionInfo.viewerCanEdit;

	return {
		ownPetition,
		canEdit,
		signed: !!signature,
	};
}

type GammaPetitionViewerInfoQueryPetition = NonNullable<GammaPetitionViewerInfoQuery['petition']>;

async function getUserPetitionInfo(
	slugOrId: string,
	{ gql: { fetch } }: UtilityContext,
): Promise<GammaPetitionViewerInfoQueryPetition> {
	const { data } = await fetch<GammaPetitionViewerInfoQuery, GammaPetitionViewerInfoQueryVariables>({
		query: GammaPetitionViewerInfo,
		variables: { slugOrId },
		rejectOnError: true,
	});
	if (!data.petition) {
		throw new NotFoundError();
	}
	return data.petition;
}
